import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  USER_ID,
  ALTERNATE_LOGIN_ID,
  FIRST_NAME,
  LAST_NAME,
  MOBILE_NO,
  LANDLINE_NO,
  EMAIL_ID_1,
  EMAIL_ID_2,
  ADDRESS_1,
  ADDRESS_2,
  ZIPCODE,
  STATE,
  CITY,
  PO_BOX,
  ADDRESS,
  USERNAME,
  SHOW_CORRESPONDENCE_ADDRESS,
  CORRESPONDENCE_ADDRESS,
} from "./consts";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useEffect, useState } from "react";
import useApis from "./useApis";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { schema } from "./validations";

const useStep2 = ({ onNextClick }: { onNextClick?: () => void }) => {
  const form = useForm<any>({
    defaultValues: {
      [USER_ID]: "",
      [ALTERNATE_LOGIN_ID]: "",
      [FIRST_NAME]: "",
      [LAST_NAME]: "",
      [MOBILE_NO]: "",
      [LANDLINE_NO]: "",
      [EMAIL_ID_1]: "",
      [EMAIL_ID_2]: undefined,
      [ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: undefined,
        [ZIPCODE]: "",
        [STATE]: "",
        [CITY]: "",
        [PO_BOX]: "",
      },
      [SHOW_CORRESPONDENCE_ADDRESS]: false,
      [CORRESPONDENCE_ADDRESS]: {
        [ADDRESS_1]: "",
        [ADDRESS_2]: "",
        [ZIPCODE]: "",
        [STATE]: "",
        [CITY]: "",
        [PO_BOX]: "",
      },
      [USERNAME]: "",
    },
    resolver: zodResolver(schema),
  });
  const [keys, setKeys] = useState<any>({
    state: Math.random() * 3,
    cState: Math.random() * 3,
  });

  const {
    userDetails,
    updateUserInfo,
    isUpdateUserPending,
    isUserDetailsLoading,
    isUserDetailsRefetching,
  } = useApis({
    onNextClick,
  });

  // Dynamically determine required fields
  const requiredFields = getRequiredFields(schema);

  const stateList = useSelector(
    (state: RootState) => state.currentData.stateData
  );
  const loggedInUserData = useSelector(
    (state: RootState) => state.currentData.loggedInUserData
  );

  const userId = loggedInUserData?.userId;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (userDetails) {
      form.reset({
        [USER_ID]: userDetails?.data?.[USER_ID] || "",
        [ALTERNATE_LOGIN_ID]: userDetails?.data?.[ALTERNATE_LOGIN_ID] || "",
        [FIRST_NAME]: userDetails?.data?.[FIRST_NAME] || "",
        [LAST_NAME]: userDetails?.data?.[LAST_NAME] || "",
        [MOBILE_NO]: userDetails?.data?.[MOBILE_NO] || "",
        [LANDLINE_NO]: userDetails?.data?.[LANDLINE_NO] || undefined,
        [EMAIL_ID_1]: userDetails?.data?.[EMAIL_ID_1] || "",
        [EMAIL_ID_2]: userDetails?.data?.[EMAIL_ID_2] || undefined,
        [ADDRESS]: {
          [ADDRESS_1]: userDetails?.data?.[ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: userDetails?.data?.[ADDRESS]?.[ADDRESS_2] || undefined,
          [ZIPCODE]: userDetails?.data?.[ADDRESS]?.[ZIPCODE] || "",
          [STATE]:
            stateList?.find(
              (state: { name: string; code: string }) =>
                state.name == userDetails?.data?.[ADDRESS]?.[STATE] ||
                state.code == userDetails?.data?.[ADDRESS]?.[STATE]
            )?.code || "",
          [CITY]: userDetails?.data?.[ADDRESS]?.[CITY] || "",
          [PO_BOX]: userDetails?.data?.[ADDRESS]?.[PO_BOX] || "",
        },
        [SHOW_CORRESPONDENCE_ADDRESS]: !!userDetails?.data?.[CORRESPONDENCE_ADDRESS],
        [CORRESPONDENCE_ADDRESS]: {
          [ADDRESS_1]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_1] || "",
          [ADDRESS_2]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ADDRESS_2] || "",
          [ZIPCODE]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[ZIPCODE] || "",
          [STATE]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[STATE] || "",
          [CITY]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[CITY] || "",
          [PO_BOX]: userDetails?.data?.[CORRESPONDENCE_ADDRESS]?.[PO_BOX] || "",
        },
        [USERNAME]: userDetails?.data?.[USERNAME] || "",
      });
      setKeys((prev: any) => ({
        ...prev,
        state: Math.random() * 2,
        city: Math.random() * 2,
        cState: Math.random() * 2,
        cCity: Math.random() * 2,
      }));
    }
  }, [userDetails]);

  const showCorrespondAddress =  useWatch({ control: form.control, name: SHOW_CORRESPONDENCE_ADDRESS });

  useEffect(() => {
    if (!showCorrespondAddress) return;

    const primary = form.getValues(ADDRESS);
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${ADDRESS_1}`, primary?.[ADDRESS_1] || "");
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${ADDRESS_2}`, primary?.[ADDRESS_2] || "");
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${CITY}`, primary?.[CITY] || "");
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${STATE}`, primary?.[STATE] || "");
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${ZIPCODE}`, primary?.[ZIPCODE] || "");
    form.setValue(`${CORRESPONDENCE_ADDRESS}.${PO_BOX}`, primary?.[PO_BOX] || "");
    setKeys((prev: any) => ({
      ...prev,
      cState: Math.random() * 2,
      cCity: Math.random() * 2,
    }));
  }, [showCorrespondAddress]);

  const onSubmit = (data: any) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    const address = {
      ...data.address,
      [STATE]: stateList.find(
        (state: any) => state.code == (_data[ADDRESS] as any)[STATE]
      )?.name,
    };

    if (!_data[SHOW_CORRESPONDENCE_ADDRESS]) {
      _data[CORRESPONDENCE_ADDRESS] = null
    }
    
    if (userId) {
      delete _data[USER_ID];
      updateUserInfo({
        id: userId,
        data: {
          ..._data,
          [ADDRESS]: address,
        },
      });
    }
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isUpdateUserPending,
    userId,
    stateList,
    isUserDetailsLoading,
    keys,
    isUserDetailsRefetching,
  };
};

export default useStep2;
